export interface GuardState {
    key: boolean;
    pointer: boolean;
    paste: boolean;
    programmatic: boolean;
    seconds: number;
}

const bit = (value: boolean): string => (value ? '1' : '0');

export function buildFlags(state: GuardState): string {
    return `k${bit(state.key)}p${bit(state.pointer)}v${bit(state.paste)}x${bit(state.programmatic)}t${state.seconds}`;
}

/**
 * Puur beslispunt achter de `x`-vlag: een veld is programmatisch gevuld als het een
 * waarde draagt terwijl er nooit een input-event aan voorafging. Losgetrokken van de
 * event-bedrading in `initFormGuard` zodat dit zonder DOM/jsdom te testen is.
 */
export function isProgrammaticFill(hasValue: boolean, sawInput: boolean): boolean {
    return hasValue && !sawInput;
}

export async function guardHash(challenge: string, flags: string): Promise<string> {
    const bytes = new TextEncoder().encode(`${challenge}|${flags}`);
    const digest = await crypto.subtle.digest('SHA-256', bytes);

    return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
}

export function initFormGuard(form: HTMLFormElement): void {
    const challenge = form.dataset.guard;
    const field = form.querySelector<HTMLInputElement>('input[name="guard"]');
    // Optional chaining beschermt tegen null/undefined, niet tegen een niet-gedeclareerde
    // identifier — als `crypto` in scope zou ontbreken gooit `crypto?.subtle` alsnog een
    // ReferenceError. `typeof` is de vorm die daar wel veilig tegen is.
    if (!challenge || !field || typeof crypto === 'undefined' || !crypto.subtle) return;

    const state: GuardState = { key: false, pointer: false, paste: false, programmatic: false, seconds: 0 };
    // Losse van state.programmatic: alleen om vast te stellen of er ooit een input-event
    // was vóórdat een change met waarde binnenkwam. state.programmatic zelf is sticky voor
    // de duur van het formulier (zoals de andere vlaggen); sawInput hoeft dat niet te zijn,
    // want die dient alleen als voorwaarde op het moment van de change.
    let sawInput = false;
    let firstAt: number | null = null;

    const update = (): void => {
        // performance.now() is een monotone bron, zodat een klokcorrectie (NTP, sleep/wake,
        // handmatige aanpassing) geen negatieve t kan opleveren die de server als invalid wegschrijft.
        if (firstAt === null) firstAt = performance.now();
        state.seconds = Math.floor((performance.now() - firstAt) / 1000);
        const flags = buildFlags(state);
        // Bewust geen await op submit: het veld loopt mee met de interactie, zodat er
        // op het moment van verzenden geen openstaande promise is.
        void guardHash(challenge, flags).then((hash) => {
            field.value = `${flags}.${hash}`;
        });
    };

    form.addEventListener('keydown', () => { state.key = true; update(); });
    form.addEventListener('pointerdown', () => { state.pointer = true; update(); });
    form.addEventListener('paste', () => { state.paste = true; update(); });
    form.addEventListener('input', () => { sawInput = true; });

    // Een veld dat gevuld raakt zonder dat er ooit een input-event was, is
    // programmatisch gezet — typisch gedrag van een headless browser dat `.value` toekent
    // en zelf een change dispatcht. Bewust getoetst op `sawInput` en niet op key/pointer/
    // paste: die dekken hoe de gebruiker het veld raakte, niet of de browser er zelf een
    // input-event voor genereerde (bijvoorbeeld browser-autofill, dat wel een input-event
    // geeft zonder key/pointer/paste).
    form.addEventListener('change', (event) => {
        const target = event.target as (HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) | null;
        if (isProgrammaticFill(!!target?.value, sawInput)) {
            state.programmatic = true;
        }
        update();
    });

    update();
}
